import React from "react";
import { Link } from "react-router-dom";
import { VERTICALS } from "@/data/verticals";
import { ArrowUpRight } from "@phosphor-icons/react";
import { Reveal } from "@/components/Reveal";
import PageHead from "@/components/PageHead";

const HeroVisual = null;

const Marquee = () => {
  const items = [
    "TCPA Compliant",
    "Real-time delivery",
    "ZIP-level routing",
    "Hot transfers",
    "CCPA",
    "GLBA",
    "Exclusive data",
    "Posted leads",
    "Live calls",
    "Owned & operated",
  ];
  const row = [...items, ...items];
  return (
    <div className="overflow-hidden border-y border-citirex-border bg-citirex-bg2/60">
      <div className="ticker flex whitespace-nowrap py-4">
        {row.map((t, i) => (
          <span
            key={i}
            className="mx-8 inline-flex items-center gap-3 font-body text-xs uppercase tracking-[0.25em] text-citirex-sub"
          >
            <span className="h-1 w-1 bg-citirex-accent" />
            {t}
          </span>
        ))}
      </div>
    </div>
  );
};

const VerticalsBento = () => (
  <section className="bg-citirex-bg" data-testid="home-verticals">
    <div className="mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-32">
      <div className="grid grid-cols-1 items-end gap-10 md:grid-cols-12">
        <div className="md:col-span-7">
          <p className="overline">Verticals</p>
          <h2 className="mt-4 font-display text-4xl font-medium leading-[1.05] md:text-6xl">
            Eight verticals.{" "}
            <span className="font-editorial italic text-citirex-accent">
              One partner.
            </span>
          </h2>
        </div>
        <div className="md:col-span-5">
          <p className="font-body text-base text-citirex-sub md:text-lg">
            We don't aggregate other people's leads. We own the funnels, the
            data and the calls — across the largest performance marketing
            verticals in the U.S.
          </p>
        </div>
      </div>

      <div className="mt-16 grid grid-cols-1 gap-px bg-citirex-border md:grid-cols-2 lg:grid-cols-4">
        {VERTICALS.map((v, i) => (
          <Link
            key={v.slug}
            to={`/verticals/${v.slug}`}
            data-testid={`home-vertical-card-${v.slug}`}
            className="group relative flex flex-col justify-between bg-citirex-bg p-8 transition-colors hover:bg-citirex-surface"
          >
            <div className="flex items-start justify-between">
              <span className="font-editorial text-sm italic text-citirex-sub">
                {v.index}
              </span>
              <ArrowUpRight
                size={20}
                weight="bold"
                className="text-citirex-ink transition-transform group-hover:rotate-45"
              />
            </div>
            <div className="mt-24">
              <h3 className="font-display text-2xl font-medium leading-tight text-citirex-ink md:text-3xl">
                {v.name}
              </h3>
              <p className="mt-3 max-w-xs font-body text-sm text-citirex-sub">
                {v.tagline}
              </p>
            </div>
            <div
              aria-hidden
              className={`absolute inset-x-0 bottom-0 h-1 origin-left scale-x-0 bg-citirex-accent transition-transform duration-500 group-hover:scale-x-100`}
            />
          </Link>
        ))}
      </div>
    </div>
  </section>
);

const ValueProps = () => {
  const items = [
    {
      n: "01",
      t: "Owned-and-operated",
      d: "We build, run and optimize our own funnels — not aggregate someone else's exhaust. Quality is engineered upstream.",
    },
    {
      n: "02",
      t: "Appetite-matched",
      d: "Carrier and contractor underwriting rules drive what we deliver. You stop paying for records you can't write.",
    },
    {
      n: "03",
      t: "Compliant by design",
      d: "TCPA, CCPA, GLBA, CMS Ch. 3 — consent records, retention and audit trails baked into every lead.",
    },
    {
      n: "04",
      t: "Performance-priced",
      d: "Data, posted leads, live calls, hot transfers and appointment-set — priced to your KPI, not ours.",
    },
  ];
  return (
    <section className="border-t border-citirex-border bg-citirex-bg">
      <div className="mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-32">
        <div className="grid grid-cols-1 gap-10 md:grid-cols-12">
          <div className="md:col-span-5">
            <p className="overline">Why Citirex</p>
            <h2 className="mt-4 font-display text-4xl font-medium leading-[1.05] md:text-6xl">
              Built for partners who measure{" "}
              <span className="font-editorial italic">bind rate</span>, not lead
              count.
            </h2>
          </div>
          <div className="md:col-span-7">
            <div className="grid grid-cols-1 gap-px bg-citirex-border sm:grid-cols-2">
              {items.map((i) => (
                <div key={i.n} className="bg-citirex-bg p-6 md:p-8">
                  <p className="font-editorial text-sm italic text-citirex-sub">
                    {i.n}
                  </p>
                  <h4 className="mt-3 font-display text-xl font-medium text-citirex-ink">
                    {i.t}
                  </h4>
                  <p className="mt-3 font-body text-sm text-citirex-sub">
                    {i.d}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const StatsBand = () => {
  return (
    <section className="bg-citirex-ink text-citirex-inv">
      <div className="mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-32">
        <p className="overline">By the numbers</p>
        <h2 className="mt-4 max-w-3xl font-display text-4xl font-medium leading-[1.05] md:text-6xl">
          Volume at scale, with the quality to match.
        </h2>
      </div>
    </section>
  );
};

const Testimonial = () => (
  <section className="border-y border-citirex-border bg-citirex-bg2">
    <div className="mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-32">
      <div className="grid grid-cols-1 gap-10 md:grid-cols-12">
        <div className="md:col-span-2">
          <p className="overline">Field notes</p>
        </div>
        <div className="md:col-span-10">
          <blockquote className="font-editorial text-3xl italic leading-tight text-citirex-ink md:text-5xl">
            “Citirex replaced four media vendors in our first quarter. Their
            funnels close — and their team treats our growth like it's theirs.”
          </blockquote>
          <div className="mt-10 flex items-center gap-4">
            <div className="h-12 w-12 bg-citirex-accent" />
            <div>
              <p className="font-body text-sm font-semibold text-citirex-ink">
                Margaret Chen
              </p>
              <p className="font-body text-xs uppercase tracking-[0.2em] text-citirex-sub">
                Chief Growth Officer · National P&C Carrier
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

export default function Home() {
  return (
    <main data-testid="home-page">
      <PageHead
        title="Premium B2B Lead Generation"
        description="Citirex is a Los Angeles based performance marketing agency generating high-intent leads, live calls and hot transfers across the eight largest insurance and home-services verticals in the U.S."
        path="/"
      />
      {/* HERO */}
      <section className="relative grain overflow-hidden border-b border-citirex-border bg-citirex-bg">
        {/* Faded skyline background */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 animate-[heroFade_1.6s_ease-out_both]"
        >
          <img
            src="https://customer-assets.emergentagent.com/job_multi-vertical-hub-1/artifacts/we6y2imo_iStock-2155951534.jpg"
            alt=""
            className="absolute inset-0 h-full w-full object-cover opacity-[0.22]"
            loading="eager"
          />
          {/* Wash to keep text on a light, editorial canvas */}
          <div className="absolute inset-0 bg-citirex-bg/40" />
          {/* Top fade into the navbar */}
          <div className="absolute inset-x-0 top-0 h-48 bg-gradient-to-b from-citirex-bg via-citirex-bg/70 to-transparent" />
          {/* Bottom fade into the marquee */}
          <div className="absolute inset-x-0 bottom-0 h-64 bg-gradient-to-t from-citirex-bg via-citirex-bg/85 to-transparent" />
          {/* Right vignette — keeps the heading column legible on the brighter skyline highlights */}
          <div className="absolute inset-y-0 left-0 hidden w-3/5 bg-gradient-to-r from-citirex-bg via-citirex-bg/85 to-transparent md:block" />
        </div>

        <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center px-6 pb-24 pt-16 md:px-12 md:pb-40 md:pt-28">
          <div className="max-w-4xl">
            <Reveal>
              <div className="inline-flex items-center gap-3 border border-citirex-border bg-citirex-surface/85 px-3 py-2 backdrop-blur">
                <span className="h-2 w-2 animate-pulse bg-citirex-accent" />
                <span className="font-body text-[11px] uppercase tracking-[0.25em] text-citirex-ink">
                  Los Angeles · Performance lead generation
                </span>
              </div>
            </Reveal>
            <Reveal delay={0.05}>
              <h1 className="mt-8 font-display text-5xl font-semibold leading-[0.95] tracking-tighter text-citirex-ink sm:text-6xl md:text-7xl lg:text-[120px]">
                Lead generation,
                <br />
                <span className="font-editorial italic font-normal text-citirex-accent">
                  performance-priced.
                </span>
              </h1>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-8 max-w-2xl font-body text-lg leading-relaxed text-citirex-sub md:text-xl">
                Citirex is a Los Angeles based performance marketing agency
                generating high-intent leads, live calls and hot transfers
                across the eight largest insurance and home-services
                verticals in the United States — built for carriers, agencies
                and contractors who buy on bind, not on volume.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="mt-10 flex flex-wrap items-center gap-4">
                <Link
                  to="/contact"
                  data-testid="hero-cta-primary"
                  className="group inline-flex items-center gap-2 bg-citirex-accent px-7 py-5 font-body text-sm font-semibold uppercase tracking-wider text-citirex-inv transition-colors hover:bg-citirex-accentDark"
                >
                  Open a media buy
                  <ArrowUpRight
                    size={18}
                    weight="bold"
                    className="transition-transform group-hover:rotate-45"
                  />
                </Link>
                <Link
                  to="/how-it-works"
                  data-testid="hero-cta-secondary"
                  className="group inline-flex items-center gap-2 border-2 border-citirex-ink bg-citirex-bg/70 px-7 py-5 font-body text-sm font-semibold uppercase tracking-wider text-citirex-ink backdrop-blur transition-colors hover:bg-citirex-ink hover:text-citirex-inv"
                >
                  How it works
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <Marquee />

      <VerticalsBento />
      <ValueProps />
      <StatsBand />
      <Testimonial />

      <section className="bg-citirex-bg" data-testid="home-secondary-cta">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-6 py-24 md:grid-cols-12 md:px-12 md:py-32">
          <div className="md:col-span-6">
            <p className="overline">Editorial</p>
            <h2 className="mt-4 font-display text-4xl font-medium leading-[1.05] md:text-6xl">
              We treat your acquisition like a{" "}
              <span className="font-editorial italic">portfolio</span>, not a
              line item.
            </h2>
          </div>
          <div className="md:col-span-6 md:pl-12">
            <p className="font-body text-base text-citirex-sub md:text-lg">
              Every vertical we run is built with a dedicated media buyer,
              compliance reviewer, and analytics lead. We weight your spend
              against your appetite — not against a vendor's inventory — and
              re-balance weekly based on what's actually binding.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-6 marker-line pt-8">
              <Link
                to="/performance"
                className="group inline-flex items-center gap-2 font-body text-sm font-semibold uppercase tracking-wider text-citirex-ink hover:text-citirex-accent"
              >
                See performance benchmarks
                <ArrowUpRight
                  size={16}
                  weight="bold"
                  className="transition-transform group-hover:rotate-45"
                />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
